// FileRow.tsx

import * as React from 'react'
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogTitle,
  Button,
  Chip,
} from '@mui/material'
import { X } from 'lucide-react'

interface FileRowProps {
  file: File
  uploadedFiles: Set<string>
  getFileSignature: (file: File) => string
  onRemove: (file: File) => void
}

const formatBytes = (bytes: number) => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

export function FileRow({ file, uploadedFiles, getFileSignature, onRemove }: FileRowProps) {
  const [openConfirm, setOpenConfirm] = React.useState(false)

  const signature = getFileSignature(file)
  const isUploaded = uploadedFiles.has(signature)

  const removeHandler = () => {
    if (isUploaded) {
      setOpenConfirm(true)
    } else {
      onRemove(file)
    }
  }

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        position: 'relative',
        border: '1px solid #ddd',
      }}
    >
      {/* Remove button */}
      <IconButton
        size='small'
        onClick={removeHandler}
        sx={{ position: 'absolute', top: 4, right: 4 }}
      >
        <X size={16} color='red' />
      </IconButton>

      {/* File icon */}
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1,
          backgroundColor: '#ff6b6b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '0.75rem',
          flexShrink: 0,
        }}
      >
        PDF
      </Box>

      {/* File info */}
      <Box sx={{ overflow: 'hidden', flexGrow: 1 }}>
        {/* Full file name on hover */}
        <Tooltip title={file.name} arrow>
          <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.9rem', cursor: 'default' }}>
            {file.name}
          </Typography>
        </Tooltip>

        <Typography variant='body2' sx={{ color: 'gray', fontSize: '0.75rem' }}>
          {formatBytes(file.size)}
        </Typography>
      </Box>

      {/* Status chip */}
      <Chip
        label={isUploaded ? 'Uploaded' : 'New'}
        size='small'
        color={isUploaded ? 'success' : 'warning'}
        sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, flexShrink: 0, mr: 2.5 }}
      />

      {/* Confirmation Dialog for uploaded files */}
      <Dialog
        open={openConfirm}
        onClose={() => {
          setOpenConfirm(false)
        }}
      >
        <DialogTitle>Are you sure you want to remove this uploaded file?</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenConfirm(false)
            }}
            color='primary'
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setOpenConfirm(false)
              onRemove(file)
            }}
            color='error'
            variant='contained'
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
